import React, { ReactElement } from "react"
import {
	Avatar,
	Box,
	Chip,
	Dialog,
	Divider,
	IconButton,
	List,
	ListItemButton,
	Tooltip,
	Typography,
} from "@mui/material"
import { Trans } from "@lingui/macro"
import { i18n } from "@lingui/core"
import {
	AssetMatchBrand,
	HotBrandResponse,
	PriorityLevel,
} from "models"
import withUseRouter, { UseRouterParams } from "services/router"
import { ResearchDetailCard } from "components/research/research-detail-card"
import { MatesComponent, MatesComponentState } from "components/mates-component"
import { Close } from "@mui/icons-material"

interface DashboardHotSituationsProps extends UseRouterParams {
	cardType: "ASSET" | "BRAND"
	data: AssetMatchBrand[] | HotBrandResponse[]
}

interface DashboardHotSituationsState extends MatesComponentState {
	selectedId: string
}

class DashboardHotSituationsInner extends React.Component<DashboardHotSituationsProps, DashboardHotSituationsState> {

	private readonly matesComponent: MatesComponent<DashboardHotSituationsProps, DashboardHotSituationsState> = new MatesComponent(this)

	constructor(props: any) {
		super(props)

		this.state = {
			...this.matesComponent.setupState(),
			selectedId: ""
		}
	}

	private getChipData = (priorityLevel: PriorityLevel): ReactElement => {
		switch (priorityLevel) {
			case PriorityLevel.URGENTE:
			case PriorityLevel.ALTA:
				return <Chip label={priorityLevel} color="error" />
			case PriorityLevel.NORMALE:
				return <Chip label={priorityLevel} sx={{ backgroundColor: "#ef832e", color: "white" }} />
			case PriorityLevel.BASSA:
				return <Chip label={priorityLevel} sx={{ backgroundColor: "#fef022" }} />
			default:
				return <Chip label={priorityLevel} />
		}
	}

	private handleClick = (id: string) => {
		if (this.props.cardType === "ASSET")
			this.props.navigate(`/match-proposal/match/${id}`)
		else {
			this.setState(() => ({
				selectedId: id
			}))
		}
		console.log((this.props.data))
	}

	private renderAssetContent = (match: AssetMatchBrand): ReactElement => (
		<>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					flexGrow: 1,
					minWidth: 0,
				}}
			>
				<Tooltip title={match.brand.name} arrow>
					{match.brand.logo ? (
						<img
							src={match.brand.logo}
							alt={`${match.brand.name} logo`}
							width={32}
							height={32}
							style={{ borderRadius: 16, objectFit: "cover" }}
						/>
					) : (
						<Avatar sx={{ fontSize: "0.6em", width: 32, height: 32 }}>
							{match.brand.name.substring(0, 2).toUpperCase()}
						</Avatar>
					)}
				</Tooltip>

				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
					<Typography sx={{ fontWeight: 600 }} noWrap>
						{match.brand.name}
					</Typography>
					<Typography variant="body2" color="text.secondary" noWrap>
						{match.asset.address}
					</Typography>
				</Box>
			</Box>
			{this.getChipData(match.priorityMatch)}
		</>
	)

	private renderBrandContent = (brandData: HotBrandResponse): ReactElement => {
		const brandName = brandData.brand?.name?.trim() || brandData.name
		const secondaryLabel = brandData.name && brandData.name !== brandName ? brandData.name : ""
		const fallbackInitials = brandName ? brandName.substring(0, 2).toUpperCase() : "?"

		return (
			<>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						flexGrow: 1,
						minWidth: 0,
					}}
				>
					<Tooltip title={brandName} arrow>
						{brandData.brand?.logo ? (
							<img
								src={brandData.brand.logo}
								alt={`${brandName} logo`}
								width={40}
								height={40}
								style={{ borderRadius: 20, objectFit: "cover" }}
							/>
						) : (
							<Avatar sx={{ fontSize: "0.6em", width: 40, height: 40 }}>
								{fallbackInitials}
							</Avatar>
						)}
					</Tooltip>

					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
						<Typography sx={{ fontWeight: 600 }} noWrap>
							{brandName}
						</Typography>
						{secondaryLabel ? (
							<Typography variant="body2" color="text.secondary" noWrap>
								{secondaryLabel}
							</Typography>
						) : null}
					</Box>
				</Box>
				{this.getChipData(brandData.priority)}
			</>
		)
	}

	render() {
		return (
			<>
				{this.state.selectedId && (
					<Dialog
						open
						onClose={() => this.setState({ selectedId: "" })}
						fullWidth
						maxWidth="md"
					>
						<Box sx={{ position: "relative", py: 6, px: 2 }}>
							<IconButton
								aria-label="close"
								onClick={() => this.setState({ selectedId: "" })}
								sx={{
									position: "absolute",
									top: 8,
									right: 8,
									color: (theme) => theme.palette.grey[500],
								}}
							>
								<Close />
							</IconButton>

							{this.props.cardType === "BRAND" ? (
								<ResearchDetailCard
									data={
										(this.props.data as AssetMatchBrand[]).find(
											(i) => i.id === this.state.selectedId
										) as any
									}
								/>
							) : null}
						</Box>
					</Dialog>
				)}
				<Box sx={{ pt: 6, height: "328px" }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							fontWeight: 600,
							px: 2,
							mb: 1,
							textTransform: "uppercase",
						}}
					>
						<Typography>
							{i18n._(`enums.dashboard-hot-situations.${this.props.cardType}`)}
						</Typography>
						<Typography>
							{i18n._({ id: "enums.dashboard-hot-situations.PRIORITY", message: "Priorità" })}
						</Typography>
					</Box>

					{this.props.data.length === 0 && (
						<Typography sx={{ textAlign: "center", mt: 5 }}>
							<Trans id="dashboard.cards-with-list.no-elements">
								Nessun elemento trovato
							</Trans>
						</Typography>
					)}

					<List sx={{ width: "100%", height: "235px", overflow: "auto", p: 0 }}>
						{this.props.data.map((item, index) => (
							<React.Fragment key={item.id}>
								<ListItemButton
									disableRipple
									disableTouchRipple
									onClick={() => this.handleClick(item.id)}
									sx={{
										backgroundColor: index % 2 ? "background.paper" : "#f4f4f4",
										p: 0,
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											py: 1,
											px: 2,
											width: "100%",
										}}
									>
										{this.props.cardType === "ASSET"
											? this.renderAssetContent(item as AssetMatchBrand)
											: this.renderBrandContent(item as HotBrandResponse)}
									</Box>
								</ListItemButton>
								<Divider />
							</React.Fragment>
						))}
					</List>
				</Box>
			</>
		)
	}
}

export const DashboardHotSituations = withUseRouter(DashboardHotSituationsInner)
